import type { PicksAction } from '@/lib/picks/picksActionTypes'

/** Loose shape for optional phone fields on actions or nested place (API / future payloads). */
type LooseCallPlace = {
  phone?: string | null
  phoneNumber?: string | null
  nationalPhoneNumber?: string | null
  internationalPhoneNumber?: string | null
  name?: string | null
}

export type CallEnrichmentPlace = {
  id?: string
  name?: string
  lat?: number
  lng?: number
  phone?: string | null
  phoneNumber?: string | null
  nationalPhoneNumber?: string | null
  internationalPhoneNumber?: string | null
}

function firstNonEmpty(
  ...vals: Array<string | null | undefined>
): string | null {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim().length > 0) return v
  }
  return null
}

/**
 * Strip to digits and leading + only (tel: body).
 * @see picks call execution — keep in sync with product spec.
 */
export function normalizePhone(phone?: string | null): string | null {
  if (!phone) return null
  const cleaned = phone.replace(/[^\d+]/g, '')
  return cleaned || null
}

/**
 * Raw phone string, in resolution order:
 * action.phoneNumber → action.place (national/international) → action.metadata?.phoneNumber
 * → payload.place → legacy action.phone / place.phone.
 */
export function extractRawCallPhone(action: PicksAction): string | null {
  const payload = (action.payload ?? {}) as Record<string, unknown>
  const payloadPlace = payload.place as LooseCallPlace | undefined
  const loose = action as PicksAction & {
    place?: LooseCallPlace
    metadata?: { phoneNumber?: string | null }
  }
  const payloadPhoneNumber =
    typeof payload.phoneNumber === 'string' ? payload.phoneNumber : null
  const payloadNational =
    typeof payload.nationalPhoneNumber === 'string'
      ? payload.nationalPhoneNumber
      : typeof payload.national_phone_number === 'string'
        ? payload.national_phone_number
        : null
  const payloadInternational =
    typeof payload.internationalPhoneNumber === 'string'
      ? payload.internationalPhoneNumber
      : typeof payload.international_phone_number === 'string'
        ? payload.international_phone_number
        : null
  const payloadFormatted =
    typeof payload.formattedPhoneNumber === 'string'
      ? payload.formattedPhoneNumber
      : typeof payload.formatted_phone_number === 'string'
        ? payload.formatted_phone_number
        : null
  return firstNonEmpty(
    action.phoneNumber,
    loose.place?.phoneNumber,
    loose.place?.nationalPhoneNumber,
    loose.place?.internationalPhoneNumber,
    loose.metadata?.phoneNumber,
    payloadPhoneNumber,
    payloadNational,
    payloadInternational,
    payloadFormatted,
    payloadPlace?.phoneNumber,
    payloadPlace?.nationalPhoneNumber,
    payloadPlace?.internationalPhoneNumber,
    action.phone,
    loose.place?.phone,
    payloadPlace?.phone
  )
}

export function callPhoneDigitCount(normalized: string): number {
  return (normalized.match(/\d/g) ?? []).length
}

/** Match existing picks behavior: need enough digits to treat as dialable. */
export function isDialableNormalizedPhone(normalized: string): boolean {
  return callPhoneDigitCount(normalized) >= 10
}

/** Normalized tel body when dialable; otherwise empty string. */
export function getResolvedCallPhoneForTel(action: PicksAction): string {
  const raw = extractRawCallPhone(action)
  if (!raw) return ''
  const normalized = normalizePhone(raw)
  if (!normalized || !isDialableNormalizedPhone(normalized)) return ''
  return normalized
}

/** Same rule as labels + executor: dialable number resolvable from the action. */
export function isCallablePhone(action: PicksAction): boolean {
  return Boolean(getResolvedCallPhoneForTel(action))
}

export function picksCallFallbackLabel(
  action: PicksAction
): 'Get directions' | 'View details' {
  const lat = action.lat
  const lng = action.lng
  if (
    lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  ) {
    return 'Get directions'
  }
  return 'View details'
}

/** When type === 'call' but there is no dialable number, open directions or Maps listing. */
export function buildPicksFallbackForMissingCallPhone(
  action: PicksAction
): PicksAction {
  const lat = action.lat
  const lng = action.lng
  if (
    lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  ) {
    return {
      type: 'navigate',
      label: 'Get directions',
      lat,
      lng,
      title: action.title,
      placeId: action.placeId,
      payload: action.payload,
    }
  }
  const url = googleMapsPlaceFallbackUrl(action)
  return {
    type: 'view_details',
    label: 'View details',
    url: url ?? undefined,
    title: action.title,
    placeId: action.placeId,
    payload: action.payload,
  }
}

export function googleMapsPlaceFallbackUrl(action: PicksAction): string | null {
  if (action.url && /^https?:\/\//i.test(action.url)) return action.url
  const pid = action.placeId?.trim()
  const title = action.title?.trim()
  if (pid && title) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(title)}&query_place_id=${encodeURIComponent(pid)}`
  }
  if (title) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(title)}`
  }
  return null
}

/** Build a call action from a place row (no hero primary merge). */
export function enrichedCallFromAgentPlace(
  place: CallEnrichmentPlace
): PicksAction {
  const ext = place as CallEnrichmentPlace & {
    phoneNumber?: string
    nationalPhoneNumber?: string
    internationalPhoneNumber?: string
  }
  return mergeCallActionWithHeroContext(
    {
      type: 'call',
      label: 'Call now',
      phoneNumber: ext.phoneNumber,
      phone: place.phone ?? undefined,
      title: place.name,
      placeId: place.id,
    },
    place
  )
}

/**
 * Canonical dial link + on-card label when the place resolves to a dialable number
 * (same merge + resolution as planner and executePicksAction).
 */
export function getCallableTelLinkFromPlace(
  place: CallEnrichmentPlace | null | undefined
): { href: string; displayText: string } | null {
  if (!place) return null
  const merged = enrichedCallFromAgentPlace(place)
  const telBody = getResolvedCallPhoneForTel(merged)
  if (!telBody) return null
  const raw = extractRawCallPhone(merged)
  const displayText = raw && raw.trim().length > 0 ? raw.trim() : telBody
  return { href: `tel:${telBody}`, displayText }
}

/**
 * Merge hero `primaryAction` with `place` so tel resolution sees both pipeline action fields
 * and entity phones (fixes label vs execution mismatch on Services/Vets hero).
 */
export function mergeCallActionWithHeroContext(
  primary: PicksAction | null | undefined,
  place: CallEnrichmentPlace | null | undefined
): PicksAction {
  if (!place) {
    if (primary?.type === 'call') return primary
    return { type: 'call', label: 'Call now', title: '' }
  }
  const base: PicksAction =
    primary?.type === 'call'
      ? primary
      : {
          type: 'call',
          label: 'Call now',
          title: place.name,
          placeId: place.id,
        }
  return enrichCallActionWithPlace(base, place)
}

/**
 * Merge `card.place` into a call action so executePicksAction can resolve phone without pipeline changes.
 */
export function enrichCallActionWithPlace(
  action: PicksAction,
  place:
    | (LooseCallPlace & {
        id?: string
        name?: string
        lat?: number
        lng?: number
      })
    | null
    | undefined
): PicksAction {
  if (action.type !== 'call') return action
  if (!place) return action
  const ext = place as LooseCallPlace & {
    id?: string
    name?: string
    lat?: number
    lng?: number
  }
  const placeSlice: LooseCallPlace & { name?: string | null } = {
    phone: place.phone,
    phoneNumber:
      ext.phoneNumber ??
      (place.phone != null && String(place.phone).trim()
        ? String(place.phone)
        : undefined),
    nationalPhoneNumber: ext.nationalPhoneNumber,
    internationalPhoneNumber: ext.internationalPhoneNumber,
    name: place.name,
  }
  return {
    ...action,
    phoneNumber:
      action.phoneNumber ??
      ext.phoneNumber ??
      (place.phone != null && String(place.phone).trim()
        ? String(place.phone)
        : undefined),
    phone: action.phone ?? place.phone ?? undefined,
    placeId: action.placeId ?? place.id,
    title: action.title ?? place.name,
    lat: action.lat ?? place.lat,
    lng: action.lng ?? place.lng,
    place: placeSlice,
    payload: {
      ...(action.payload ?? {}),
      place: {
        phone: place.phone,
        phoneNumber:
          place.phoneNumber ??
          (place.phone != null && String(place.phone).trim()
            ? String(place.phone)
            : undefined),
        nationalPhoneNumber: place.nationalPhoneNumber,
        internationalPhoneNumber: place.internationalPhoneNumber,
      },
    },
  }
}
